import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { Button, Card, Icon, Input, Stepper, useToast } from "@xinghai/ui";
import { friendlyMessage } from "../../lib/apiErrors";
import { homeFor, primaryRole } from "../../app/roles";
import { needsWarmup, onboardingIndex, onboardingSteps } from "../student/onboarding";
import { useAuth, useSignConsent } from "./useAuth";
import {
  CONSENT_AGREE_LABEL,
  CONSENT_CLAUSES,
  CONSENT_SUMMARY,
  CONSENT_TITLE,
  GUARDIAN_TEXT,
} from "./consentText";

/**
 * docs/UX.md §1.1 + owner decision: one agreement, one signature.
 * The three uses stay named in the summary and as numbered clauses; the API
 * contract is unchanged (红线 14.6 — all three flags go out together).
 */
export function ConsentPage() {
  const { me, isLoading, consentRequired, isMinor } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const sign = useSignConsent();

  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [guardianChecked, setGuardianChecked] = useState(false);
  const [guardianName, setGuardianName] = useState("");

  if (isLoading) return <div className="xz-container gz-muted">正在加载…</div>;
  if (!me) return <Navigate to="/login" replace />;

  const isStudent = me.user.roles.includes("student");
  const afterConsent = isStudent
    ? needsWarmup(me.warmup_required)
      ? "/s/warmup"
      : "/s"
    : homeFor(primaryRole(me.user.roles));
  if (!consentRequired) {
    return <Navigate to={afterConsent} replace />;
  }

  const guardianOk = !isMinor || (guardianChecked && guardianName.trim().length > 0);
  const canSubmit = agreed && guardianOk && !sign.isPending;

  const submit = async () => {
    try {
      await sign.mutateAsync({
        train: true,
        display: true,
        commercial: true,
        ...(isMinor ? { minor_guardian_signed: true } : {}),
      });
      toast.success(
        needsWarmup(me.warmup_required)
          ? "签署完成，接下来花一分钟把录音环境调好。"
          : "签署完成。",
      );
      void navigate(afterConsent, { replace: true });
    } catch (err) {
      toast.error(friendlyMessage(err, "提交没有成功，请检查网络后重试。"));
    }
  };

  return (
    <div className="xz-container xz-container--readable xz-stack">
      {isStudent && (
        <Stepper
          steps={onboardingSteps("consent")}
          current={onboardingIndex("consent")}
          label="开始前的四步"
        />
      )}

      <h1 className="gz-type-heading">{CONSENT_TITLE}</h1>
      <div className="gz-type-reading xz-consent__summary">
        {CONSENT_SUMMARY.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <button
        type="button"
        className="gz-button gz-button--quiet xz-consent__more"
        aria-expanded={termsOpen}
        onClick={() => setTermsOpen((v) => !v)}
      >
        <Icon id={termsOpen ? "ui-collapse" : "ui-expand"} size={16} />
        {termsOpen ? "收起完整条款" : "查看完整条款"}
      </button>

      {termsOpen && (
        <div className="xz-consent__full gz-type-reading">
          {CONSENT_CLAUSES.map((c) => (
            <section key={c.number}>
              <h2 className="gz-type-ui">
                {c.number}、{c.title}
              </h2>
              {c.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </section>
          ))}
        </div>
      )}

      {isMinor && (
        <Card title="监护人联签" padding="md">
          <p className="gz-muted">{GUARDIAN_TEXT}</p>
          <label className="xz-consent__row">
            <input
              type="checkbox"
              checked={guardianChecked}
              onChange={(e) => setGuardianChecked(e.target.checked)}
            />
            <span>监护人已经阅读以上内容并同意。</span>
          </label>
          <label className="gz-field">
            <span>监护人姓名</span>
            <Input
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              autoComplete="off"
              placeholder="请监护人填写"
            />
          </label>
        </Card>
      )}

      <Card padding="md">
        <label className="xz-consent__row xz-consent__agree">
          <input
            type="checkbox"
            checked={agreed}
            aria-label={CONSENT_AGREE_LABEL}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span>{CONSENT_AGREE_LABEL}</span>
        </label>
      </Card>

      <Button
        variant="primary"
        size="lg"
        block
        disabled={!canSubmit}
        loading={sign.isPending}
        onClick={() => void submit()}
      >
        签署并继续
      </Button>
      {!agreed && <p className="gz-caption">勾选之后才能签署。</p>}
    </div>
  );
}
